import dayjs, { type Dayjs } from "dayjs";
import type { TeleconsultoriaReceivedInvite } from "../types";

/** Momento do convite em relação a agora. Também define a cor do card. */
export type ReceivedInviteTone = "now" | "today" | "upcoming" | "past" | "none";

export interface ReceivedInviteGroup {
  key: ReceivedInviteTone;
  title: string;
  tone: ReceivedInviteTone;
  items: TeleconsultoriaReceivedInvite[];
  /** "Anteriores" começa recolhido para não competir com o que é urgente. */
  defaultCollapsed: boolean;
}

/** Janela de "Acontecendo agora": começou há até 2h ou começa em até 15 min. */
const NOW_WINDOW_BEFORE_HOURS = 2;
const NOW_WINDOW_AFTER_MINUTES = 15;

/** Ordem de exibição dos grupos. */
const GROUP_META: Array<Omit<ReceivedInviteGroup, "items">> = [
  { key: "now", tone: "now", title: "Acontecendo agora", defaultCollapsed: false },
  { key: "today", tone: "today", title: "Mais tarde hoje", defaultCollapsed: false },
  { key: "upcoming", tone: "upcoming", title: "Próximos dias", defaultCollapsed: false },
  { key: "past", tone: "past", title: "Anteriores em aberto", defaultCollapsed: true },
  { key: "none", tone: "none", title: "Sem horário", defaultCollapsed: false },
];

function toneOf(invite: TeleconsultoriaReceivedInvite, now: Dayjs): ReceivedInviteTone {
  const start = invite.start ? dayjs(invite.start) : undefined;
  if (!start?.isValid()) return "none";
  if (start.isBefore(now.subtract(NOW_WINDOW_BEFORE_HOURS, "hour"))) return "past";
  if (!start.isAfter(now.add(NOW_WINDOW_AFTER_MINUTES, "minute"))) return "now";
  if (start.isSame(now, "day")) return "today";
  return "upcoming";
}

function startTime(invite: TeleconsultoriaReceivedInvite): number {
  return invite.start ? dayjs(invite.start).valueOf() : 0;
}

/**
 * Separa os convites por momento. Grupos vazios não voltam.
 * Crescente em agora/hoje/próximos; decrescente em "Anteriores" (mais recente primeiro).
 */
export function groupReceivedInvites(
  invites: TeleconsultoriaReceivedInvite[],
  now: Dayjs,
): ReceivedInviteGroup[] {
  const byTone = new Map<ReceivedInviteTone, TeleconsultoriaReceivedInvite[]>();
  invites.forEach((invite) => {
    const tone = toneOf(invite, now);
    byTone.set(tone, [...(byTone.get(tone) || []), invite]);
  });

  return GROUP_META.map((meta) => {
    const items = [...(byTone.get(meta.key) || [])].sort((a, b) =>
      meta.key === "past"
        ? startTime(b) - startTime(a)
        : startTime(a) - startTime(b),
    );
    return { ...meta, items };
  }).filter((group) => group.items.length > 0);
}
